/*----------------------------------
- MARKETING PAGE: THE FAQ EXPAND EVENT
----------------------------------*/

/**
 * The one `faq_expand` listener of the website's question lists (batch 6 S2b, D: Consistency C8, batch 7 review m-C7:
 * it existed five times, on pricing, the product feature and solutions pages, Appraise, Home and the compare pages).
 * The marketing `FaqList` takes no per-answer handler and `toggle` does not bubble, so one capture listener on the
 * list's wrapper hears every `details` below it (Preact types no `onToggleCapture`, hence the listener).
 *
 * @docs docs/features/website-marketing-layer
 * @rule One event per open: a closing answer sends nothing, and the listener subscribes once per mount, reading the
 *       latest questions from a ref, so a caller that rebuilds its list each render never stacks a second listener.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useEffect, useRef } from 'preact/hooks';

import { track } from '@/client/pages/landing/lib/analytics';

/*----------------------------------
- TYPES
----------------------------------*/

/** One answer of the list: its `details` id and the question the event names. */
export type FaqExpandEntry = { id: string; question: string };

/*----------------------------------
- HELPERS
----------------------------------*/

/** The question a `toggle` opened, or null for a closing answer, another element or an id the list does not hold. */
export const resolveOpenedQuestion = (target: EventTarget | null, questions: ReadonlyMap<string, string>): string | null =>
    target instanceof HTMLDetailsElement && target.open ? (questions.get(target.id) ?? null) : null;

/** The capture handler: names the opened question once, from the questions the getter reads at the event. */
export const createFaqExpandListener = (questions: () => ReadonlyMap<string, string>, emit: (question: string) => void) => (event: Event) => {
    const question = resolveOpenedQuestion(event.target, questions());
    if (question) emit(question);
};

/*----------------------------------
- HOOKS
----------------------------------*/

/**
 * Returns the ref of the element that wraps the list's `details` (its section or the list's own wrapper). A page whose
 * wrapper takes no ref (the marketing `PageWrap` and `Section`) names it by `rootId` instead, and leaves the ref unused.
 */
export const useFaqExpand = <T extends HTMLElement>(entries: readonly FaqExpandEntry[], rootId?: string) => {
    const rootRef = useRef<T>(null);
    const questionsRef = useRef<ReadonlyMap<string, string>>(new Map());
    questionsRef.current = new Map(entries.map((entry) => [entry.id, entry.question]));

    useEffect(() => {
        const root = rootId ? document.getElementById(rootId) : rootRef.current;
        if (!root) return undefined;
        const onToggle = createFaqExpandListener(() => questionsRef.current, (question) => track('faq_expand', { question }));
        root.addEventListener('toggle', onToggle, true);
        return () => root.removeEventListener('toggle', onToggle, true);
    }, [rootId]);

    return rootRef;
};
